import { camelCase, sortBy, upperFirst } from 'lodash'
import { splitLines } from './functional/string.no-deps'
import { pipe } from './functional/pipe.no-deps'

export function pascalCase(str: string) {
    return pipe(str, camelCase, upperFirst)
}

export function insertAt(stringToInsertInto: string, index: number, stringToInsert: string) {
    const before = stringToInsertInto.substring(0, index)
    const after = stringToInsertInto.substring(index)
    return before + stringToInsert + after
}

export function insertBefore(stringToInsertInto: string, stringToFind: string, stringToInsert: string) {
    const index = stringToInsertInto.indexOf(stringToFind)
    if (index < 0) throw new Error(`Count not find ${stringToFind} in ${stringToInsertInto}`)
    return insertAt(stringToInsertInto, index, stringToInsert)
}

export function insertAfter(stringToInsertInto: string, stringToFind: string, stringToInsert: string) {
    const index = stringToInsertInto.indexOf(stringToFind)
    if (index < 0) throw new Error(`Count not find ${stringToFind} in ${stringToInsertInto}`)
    return insertAt(stringToInsertInto, index + stringToFind.length, stringToInsert)
}

export function getLastNLines(str: string, n: number) {
    for (let i = str.length; i >= 0; i--) {
        // If not line ending, move on
        if (str[i] !== '\n') continue

        // Decrement the number of lines left
        n--

        // If no more lines, then return
        if (n === 0) return str.slice(i + 1, str.length)
    }

    return str
}

export function sortAlphabetical<T>(items: T[], getProperty: (item: T) => string): T[] {
    return sortBy(items, (item) => getProperty(item).toLocaleLowerCase())
}

export function ellipsisIfLong(str: string, cutOff: number): string {
    if (str == null) return str
    return str.length > cutOff ? str.slice(0, cutOff) + '... Too long' : str
}

export function splitOnFirstOccurrence(string: string, searchString: string): [string, string] {
    const index = string.indexOf(searchString)
    if (index === -1) return [string, undefined]
    return [string.slice(0, index), string.slice(index + 1)]
}

/**
 * Returns the indexes of the tokens in the string.
 *
 * Example: indexesOf('hello world', ['l', 'o', 'z']) => { l: [2, 3, 9], o: [4, 7], z: [] }
 */
export function indexesOf(str: string, tokens: string[]): Record<string, number[]> {
    const indexes: Record<string, number[]> = {}

    for (const token of tokens) {
        indexes[token] = []
        let index = str.indexOf(token)
        while (index !== -1) {
            indexes[token].push(index)
            index = str.indexOf(token, index + 1)
        }
    }

    return indexes
}

export function hasLowerCase(str: string) {
    return str !== str.toUpperCase()
}

export function stripNull(str: string) {
    return str.replace(/\0/g, '')
}

export function splitAtIndexes(s: string, indexes: number[]): string[] {
    const result = []
    let start = 0

    for (const index of indexes) {
        result.push(s.slice(start, index))
        start = index + 1
    }

    return result
}

/**
 * Find line and char number of a pattern in a string
 * Line and char numbers are 1-indexed
 */
export function findLineAndChar(text: string, pattern: string) {
    let charCount = text.indexOf(pattern)
    if (charCount === -1) return { line: -1, char: -1 }

    const lines = splitLines(text)

    for (let lineNum = 0; lineNum < lines.length; lineNum++) {
        const line = lines[lineNum]

        if (charCount < line.length) {
            return { line: lineNum + 1, char: charCount + 1 }
        }

        charCount -= line.length + 1
    }

    return { line: -1, char: -1 }
}
